import { ChangeEvent, useEffect, useState } from 'react'
import {
  CardContent,
  CardHeader,
  Card,
  Button,
  MenuItem,
  Alert,
  CardActions,
  Grid,
  TextField,
  Checkbox,
  FormControlLabel,
  Stack,
  AlertTitle
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Project, ProjectMembership } from '../../types'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../redux'
import { createWarningNotification } from '../../redux/slices/notifications'
import { getMyProjects, getProjects } from '../../redux/slices/projects'
import CreateProjectButton from './CreateProjectButton'
import useAuth from '../../hooks/useAuth'
import { usePolicy } from '../../contexts/PolicyContext'
import { ProjectReducerType } from '../../enums/ProjectReducerType'
import { isProjectActive } from '../../utils/project'

export interface SelectProjectStepProps {
  value?: number | null
  onChange: (projectId: number | undefined) => void
  onCancel?: () => void
  onBack?: () => void
  onNext: () => void
}

export default function SelectProjectStep({
  value,
  onChange,
  onCancel,
  onBack,
  onNext
}: SelectProjectStepProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation()

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The navigate function.
   */
  const navigate = useNavigate()

  /**
   * The auth state.
   */
  const { currentUser } = useAuth()

  /**
   * The policy state.
   */
  const { isSuperadmin } = usePolicy()

  /**
   * The active projects.
   */
  const activeProjects = useSelector((state: RootState) => state.projects.activeProjects)

  /**
   * The nearby projects.
   */
  const nearbyProjects = useSelector((state: RootState) => state.projects.projects)

  /**
   * The sorted nearby projects.
   */
  const sortedNearbyProjects = nearbyProjects.toSorted((a, b) => a.name.localeCompare(b.name))

  /**
   * The sorted active projects.
   */
  const baseProjects = isSuperadmin ? nearbyProjects.filter(isProjectActive) : activeProjects

  const sortedActiveProjects = baseProjects.toSorted((a, b) => a.name.localeCompare(b.name))

  /**
   * The ID of the selected project.
   */
  const [selectedProjectId, setSelectedProjectId] = useState<number | undefined>(value ?? undefined)

  /**
   * Indicates if the checkbox is checked.
   */
  const [isChecked, setIsChecked] = useState(false)

  /**
   * Indicates if the current user is memeber of the selected project.
   */
  const isMemberOfSelectedProject =
    currentUser?.projects?.some(
      (membership: ProjectMembership) => membership?.projectId === selectedProjectId
    ) ?? false

  /**
   * Indicates if the current user has access to the project.
   */
  const hasAccessToProject = isSuperadmin || isMemberOfSelectedProject

  /**
   * The ID of the active project.
   */
  const activeProjectId = currentUser?.activeProjectMembership?.projectId ?? undefined

  /**
   * Handle the location change.
   */
  const handleLocationChange = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position: GeolocationPosition) => {
          dispatch(
            getProjects({
              lat: position.coords.latitude,
              lon: position.coords.longitude,
              showDeleted: false
            })
          )
        },
        () => {
          dispatch(createWarningNotification(t('Manufacturing.locationNotFound')))
        }
      )
    } else {
      dispatch(createWarningNotification(t('Manufacturing.notSupported')))
    }
  }

  /**
   * Handle the checkbox change.
   */
  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    setIsChecked(event.target.checked)
    setSelectedProjectId(undefined)
    onChange(undefined)

    if (event.target.checked) {
      handleLocationChange()
    }
  }

  /**
   * Handle the select change.
   */
  const handleSelectChange = (event: ChangeEvent<HTMLInputElement>) => {
    const projectId = parseInt(event.target.value.toString())
    setSelectedProjectId(projectId)
    onChange(projectId)
  }

  /**
   * Handle the next action.
   */
  const handleNext = () => {
    onNext()
  }

  /**
   * Handle the cancel action.
   */
  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    } else {
      navigate('/')
    }
  }

  const handleBack = () => {
    if (onBack) {
      onBack()
    }
  }

  function handleAfterCreate(project: Project) {
    onChange(project.id)
    onNext()
  }

  useEffect(() => {
    if (isSuperadmin) {
      dispatch(getProjects({ showDeleted: false }))
    } else if (currentUser) {
      dispatch(getMyProjects(ProjectReducerType.ACTIVE_PROJECTS))
    }
  }, [currentUser, isSuperadmin])

  // Set the active project as the selected project.
  useEffect(() => {
    if (!isChecked && selectedProjectId === undefined && activeProjectId !== undefined) {
      setSelectedProjectId(activeProjectId)
      onChange(activeProjectId)
    }
  }, [isChecked, selectedProjectId, activeProjectId])

  useEffect(() => {
    if (isChecked && nearbyProjects.length === 1) {
      setSelectedProjectId(nearbyProjects[0].id)
      onChange(nearbyProjects[0].id)
    }
  }, [isChecked, nearbyProjects])

  return (
    <Card>
      <CardHeader title={t('selectProject')} />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {selectedProjectId !== undefined && !hasAccessToProject && (
              <Alert severity="error">
                <AlertTitle>{t('noAccessToProject')}</AlertTitle>
              </Alert>
            )}

            {isChecked ? (
              <>
                {sortedNearbyProjects.length > 0 ? (
                  <TextField
                    select
                    label={t('project')}
                    value={selectedProjectId ?? ''}
                    fullWidth
                    onChange={handleSelectChange}
                  >
                    {sortedNearbyProjects.map((project) => (
                      <MenuItem key={project.id} value={project.id}>
                        {project.name}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <Alert severity="warning">{t('noProjectsNearby')}</Alert>
                )}
              </>
            ) : (
              <TextField
                select
                label={t('project')}
                value={selectedProjectId ?? ''}
                fullWidth
                onChange={handleSelectChange}
              >
                {sortedActiveProjects.map((project) => (
                  <MenuItem key={project.id} value={project.id}>
                    {project.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Grid>
          <Grid item xs={12}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent={{ xs: 'flex-start', md: 'space-between' }}
              alignItems={{ xs: 'flex-start', md: 'center' }}
            >
              <FormControlLabel
                control={
                  <Checkbox checked={isChecked} value={isChecked} onChange={handleCheckboxChange} />
                }
                label={t('showOnlyNearbyProjects') as string}
              />

              <CreateProjectButton variant="text" afterCreate={handleAfterCreate} />
            </Stack>
          </Grid>
        </Grid>
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end' }}>
        <>
          {onBack && (
            <Button variant="text" color="inherit" onClick={handleBack}>
              {t('installation:back')}
            </Button>
          )}

          <Button variant="text" color="inherit" onClick={handleCancel}>
            {t('cancel')}
          </Button>

          <Button
            variant="contained"
            color="primary"
            disabled={
              selectedProjectId === undefined || selectedProjectId === null || !hasAccessToProject
            }
            onClick={handleNext}
          >
            {t('next')}
          </Button>
        </>
      </CardActions>
    </Card>
  )
}
